/**
 * Notifications page.
 *
 * Two sections:
 *  - "Needs your review" — flagged submissions still awaiting an
 *    approve/remove decision. Always shown regardless of read state.
 *  - Everything else — informational notifications (new submissions,
 *    challenges ending soon, getting outjemmed) plus already-resolved
 *    flagged submissions, newest first.
 */
import { NotificationItem } from '@/components/NotificationItem';
import { NotificationPanel } from '@/components/NotificationPanel';
import { useNotifications } from '@/hooks/useNotifications';

export function NotificationsPage() {
  const { notifications, isLoading, markRead, takeAction } = useNotifications();

  const needsReview = notifications.filter(
    (n) => n.type === 'submission_flagged' && !n.actionTaken,
  );
  const rest = notifications.filter((n) => !(n.type === 'submission_flagged' && !n.actionTaken));

  const handleApprove = (notificationId: string) => takeAction(notificationId, 'approve');
  const handleRemove = (notificationId: string) => takeAction(notificationId, 'remove');
  const handleClose = (notificationId: string) => markRead(notificationId);

  return (
    <>
      <h1 className="mb-2 text-2xl font-bold text-slate-900">Notifications</h1>
      <p className="mb-6 text-slate-600">
        Flagged submissions on your challenges, new entries, and updates on challenges you're
        following.
      </p>

      {isLoading ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center">
          <p className="text-slate-500">Loading notifications…</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center">
          <p className="text-slate-500">You're all caught up.</p>
          <p className="mt-2 text-sm text-slate-400">
            New activity on your challenges will show up here.
          </p>
        </div>
      ) : (
        <>
          {needsReview.length > 0 && (
            <div className="mb-8">
              <h2 className="mb-4 text-lg font-semibold text-slate-900">
                Needs your review ({needsReview.length})
              </h2>
              <div className="space-y-4">
                {needsReview.map((notification) => (
                  <NotificationPanel
                    key={notification.id}
                    notification={notification}
                    onApprove={handleApprove}
                    onRemove={handleRemove}
                    onClose={handleClose}
                  />
                ))}
              </div>
            </div>
          )}

          {rest.length > 0 && (
            <div>
              {needsReview.length > 0 && (
                <h2 className="mb-4 text-lg font-semibold text-slate-900">Earlier</h2>
              )}
              <div className="space-y-3">
                {rest.map((notification) =>
                  notification.type === 'submission_flagged' ? (
                    <NotificationPanel
                      key={notification.id}
                      notification={notification}
                      onApprove={handleApprove}
                      onRemove={handleRemove}
                      onClose={handleClose}
                    />
                  ) : (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onRead={markRead}
                    />
                  ),
                )}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
